/**
 * `/oms/system-status` — health-at-a-glance for the OMS substrate.
 * (OCP-139)
 *
 * Operators check this when SES throttles, when bounces spike, or
 * when a customer reports "my mail isn't sending".
 *
 * Sections:
 *   - Send quota per region (max-send-24h, max-send-rate, sent-last-24h)
 *   - IP warming status (operator-set, post-Day-1)
 *   - Recent rejected sends aggregated by rejected_reason
 *   - Configuration audit (env-var presence, never values)
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../../lib/api';
import { UnconfiguredEmptyState, formatDate } from './CustomerDetailPage';

interface SystemStatus {
  ses_quota_by_region?: Record<
    string,
    {
      max_send_24h?: number;
      max_send_rate?: number;
      sent_last_24h?: number;
    }
  > | null;
  ip_warming?: Array<{
    ip: string;
    region?: string | null;
    warming_progress_pct?: number | null;
    status?: string | null;
  }> | null;
  recent_errors?: Array<{
    rejected_reason: string;
    count: number;
    last_seen_at?: string | null;
  }> | null;
  env_audit?: Record<string, boolean> | null;
}

export function OmsSystemStatusPage() {
  const [data, setData] = useState<SystemStatus | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<SystemStatus>('/v1/admin/oms/system-status');
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown');
      }
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // OCP-779 - the revocation list is OCP-side state and matters most
  // exactly when OMS is unreachable or unconfigured, so it renders in
  // that branch too.
  if (state === 'unconfigured') {
    return (
      <div className="flex flex-col gap-4">
        <UnconfiguredEmptyState />
        <MailKeyRevocationsSection />
      </div>
    );
  }

  return (
    <div>
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · System Status</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Send quotas, IP warming, recent errors, and env-var audit.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Refresh
        </button>
      </header>

      {state === 'loading' && !data && (
        <p className="text-sm text-gray-500">Loading…</p>
      )}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">Could not load system status</h2>
          {errorDetail && (
            <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{errorDetail}</p>
          )}
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          <SesQuotaSection quotas={data.ses_quota_by_region ?? null} />
          <IpWarmingSection ips={data.ip_warming ?? null} />
          <RecentErrorsSection errors={data.recent_errors ?? null} />
          <EnvAuditSection env={data.env_audit ?? null} />
        </div>
      )}
      <div className="mt-4">
        <MailKeyRevocationsSection />
      </div>
    </div>
  );
}

interface Revocation {
  connection_id: string;
  customer_id: string;
  reason: string;
  status: 'pending' | 'confirmed';
  attempts: number;
  last_error: string | null;
  next_attempt_at: string | null;
  requested_at: string;
  confirmed_at: string | null;
}

/**
 * OCP-779 - OAuth Mail connections the customer ended whose OMS bearer key
 * OMS has not yet confirmed revoked. Each pending row is a key that may
 * still send. The sweep job retries every minute with backoff; "Retry now"
 * forces one attempt (audited, reason required) - same call as the
 * `ocp_retry_oms_connection_revocation` MCP tool.
 */
export function MailKeyRevocationsSection() {
  const [items, setItems] = useState<Revocation[] | null>(null);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ items: Revocation[]; pending: number }>(
        '/v1/admin/oms/connection-revocations?status=pending',
      );
      setItems(r.items);
      setPending(r.pending);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Could not load revocations');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function retry(connectionId: string): Promise<void> {
    const reason = window.prompt('Why retry this revocation now? (audited)');
    if (!reason || reason.trim().length < 3) return;
    setBusy(connectionId);
    try {
      await api(`/v1/admin/oms/connection-revocations/${encodeURIComponent(connectionId)}/retry`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
    } catch (err) {
      setError(err instanceof ApiError ? `Retry failed (HTTP ${err.status})` : 'Retry failed');
    } finally {
      setBusy(null);
      await load();
    }
  }

  return (
    <Card title={`Mail key revocations pending at OMS (${pending})`}>
      {error && <p className="mb-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
      {items === null && !error && <p className="text-gray-500">Loading…</p>}
      {items !== null && items.length === 0 && (
        <p className="text-gray-500" data-testid="revocations-empty">
          Every revoked Mail connection is confirmed revoked at OMS.
        </p>
      )}
      {items !== null && items.length > 0 && (
        <>
          <p className="mb-2 text-xs text-amber-800 dark:text-amber-300">
            Each row is a customer-revoked Mail key that OMS has not confirmed yet - it may still send.
          </p>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1">Connection</th>
                <th className="py-1">Account</th>
                <th className="py-1">Reason</th>
                <th className="py-1">Attempts</th>
                <th className="py-1">Last error</th>
                <th className="py-1">Next attempt</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.connection_id} data-testid={`revocation-${r.connection_id}`} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1 font-mono">{r.connection_id}</td>
                  <td className="py-1 font-mono">{r.customer_id}</td>
                  <td className="py-1">{r.reason}</td>
                  <td className="py-1 tabular-nums">{r.attempts}</td>
                  <td className="py-1 break-all">{r.last_error ?? '-'}</td>
                  <td className="py-1">{r.next_attempt_at ? formatDate(r.next_attempt_at) : '-'}</td>
                  <td className="py-1 text-right">
                    <button
                      type="button"
                      disabled={busy === r.connection_id}
                      onClick={() => void retry(r.connection_id)}
                      className="rounded border border-gray-300 dark:border-gray-700 px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                    >
                      Retry now
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <header className="border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      </header>
      <div className="px-4 py-3 text-sm">{children}</div>
    </section>
  );
}

function SesQuotaSection({
  quotas,
}: {
  quotas: Record<
    string,
    { max_send_24h?: number; max_send_rate?: number; sent_last_24h?: number }
  > | null;
}) {
  if (!quotas || Object.keys(quotas).length === 0) {
    return (
      <Card title="Send quota per region">
        <p className="text-gray-500">No send-quota data available.</p>
      </Card>
    );
  }
  return (
    <Card title="Send quota per region">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {Object.entries(quotas).map(([region, q]) => {
          const used = q.sent_last_24h ?? 0;
          const total = q.max_send_24h ?? 0;
          const pct = total > 0 ? (used / total) * 100 : 0;
          const tone =
            pct >= 95
              ? 'bg-red-500'
              : pct >= 80
                ? 'bg-amber-500'
                : 'bg-emerald-500';
          const borderTone =
            pct >= 95
              ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30/30'
              : pct >= 80
                ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30/30'
                : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900';
          return (
            <div
              key={region}
              className={`rounded border ${borderTone} px-3 py-2.5`}
            >
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400">
                  {region}
                </span>
                <span className="text-xs text-gray-500">
                  {q.max_send_rate ?? '—'} /sec
                </span>
              </div>
              <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className={`h-full ${tone}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs tabular-nums text-gray-700 dark:text-gray-300">
                <span>
                  {used.toLocaleString()} / {total.toLocaleString()} per 24h
                </span>
                <span>{pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function IpWarmingSection({
  ips,
}: {
  ips: Array<{
    ip: string;
    region?: string | null;
    warming_progress_pct?: number | null;
    status?: string | null;
  }> | null;
}) {
  if (!ips || ips.length === 0) {
    return (
      <Card title="IP warming">
        <p className="text-gray-500">
          Shared-pool — no warming applicable. Dedicated IPs land post-Day-1.
        </p>
      </Card>
    );
  }
  return (
    <Card title="IP warming">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead className="text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="py-1">IP</th>
            <th className="py-1">Region</th>
            <th className="py-1">Progress</th>
            <th className="py-1">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {ips.map((ip) => (
            <tr key={ip.ip}>
              <td className="py-1.5 font-mono text-xs">{ip.ip}</td>
              <td className="py-1.5 text-gray-600 dark:text-gray-400">{ip.region ?? '—'}</td>
              <td className="py-1.5">
                {ip.warming_progress_pct != null
                  ? `${ip.warming_progress_pct.toFixed(0)}%`
                  : '—'}
              </td>
              <td className="py-1.5">{ip.status ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </Card>
  );
}

function RecentErrorsSection({
  errors,
}: {
  errors: Array<{
    rejected_reason: string;
    count: number;
    last_seen_at?: string | null;
  }> | null;
}) {
  return (
    <Card title="Recent rejected-send aggregation">
      {!errors || errors.length === 0 ? (
        <p className="text-gray-500">No rejected sends in the recent window.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Reason</th>
              <th className="py-1 text-right">Count</th>
              <th className="py-1 text-right">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {errors.map((e) => (
              <tr key={e.rejected_reason}>
                <td className="py-1.5">{e.rejected_reason}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {e.count.toLocaleString()}
                </td>
                <td className="py-1.5 text-right text-gray-500">
                  {e.last_seen_at ? formatDate(e.last_seen_at) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  );
}

function EnvAuditSection({ env }: { env: Record<string, boolean> | null }) {
  if (!env) {
    return (
      <Card title="Configuration audit">
        <p className="text-gray-500">No env audit data.</p>
      </Card>
    );
  }
  const entries = Object.entries(env);
  return (
    <Card title="Configuration audit">
      <p className="mb-2 text-xs text-gray-500">
        Presence-only — values are never shown. Missing vars indicate operator
        action needed on the orboto Mail Coolify project.
      </p>
      <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
        {entries.map(([k, present]) => (
          <div key={k} className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 dark:bg-gray-800 px-2 py-1.5">
            <code className="text-xs">{k}</code>
            {present ? (
              <span className="rounded bg-emerald-50 dark:bg-emerald-900/30 px-2 text-xs text-emerald-800 dark:text-emerald-300">
                set
              </span>
            ) : (
              <span className="rounded bg-red-50 dark:bg-red-900/30 px-2 text-xs text-red-800 dark:text-red-300">
                missing
              </span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
